import type { BehaviorNode } from "../blueprint/types.js";
import { createValueCompiler } from "./compileValue.js";
import type {
  CompiledListFilterRule,
  CompiledListSortRule,
  CompiledValue,
  IndexedGraph,
  SceneCatalogDependencyIndex,
} from "./types.js";

function isSetFilterNode(node: BehaviorNode): boolean {
  return (
    node.type === "action" && node.kind === "set" && node.key === "setFilter"
  );
}

function isSetSortNode(node: BehaviorNode): boolean {
  return (
    node.type === "action" && node.kind === "set" && node.key === "setSort"
  );
}

function isSetListSourceNode(node: BehaviorNode): boolean {
  return (
    node.type === "action" &&
    node.kind === "set" &&
    node.key === "setListSource"
  );
}

function hasConnectedValuePort(indexed: IndexedGraph, nodeId: string): boolean {
  return indexed.inputConnectionByNodeAndPort.has(`${nodeId}:value`);
}

function readPortDefaultValue(node: BehaviorNode, portIds: string[]): unknown {
  for (const portId of portIds) {
    const port = node.ports.find((entry) => entry.id === portId);
    if (port?.value != null) {
      return port.value;
    }
  }

  return undefined;
}

/**
 * Compiles the last connected computed-source action in graph iteration order.
 *
 * A single source is intentional: unlike filters and sort rules, source
 * replacement is not cumulative.
 */
export function compileListSource(
  indexed: IndexedGraph,
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledValue | undefined {
  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  let source: CompiledValue | undefined;

  for (const node of indexed.nodesById.values()) {
    if (
      !isSetListSourceNode(node) ||
      !hasConnectedValuePort(indexed, node.id)
    ) {
      continue;
    }
    source = valueCompiler.compileInputValue(node.id, "value");
  }

  return source;
}

/**
 * Compiles `setFilter` actions into per-row predicate rules.
 */
export function compileListFilterRules(
  indexed: IndexedGraph,
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledListFilterRule[] {
  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  const rules: CompiledListFilterRule[] = [];

  for (const node of indexed.nodesById.values()) {
    if (!isSetFilterNode(node)) {
      continue;
    }

    if (!hasConnectedValuePort(indexed, node.id)) {
      continue;
    }

    const rawOrder = readPortDefaultValue(node, ["order"]);
    const order =
      typeof rawOrder === "number" && Number.isFinite(rawOrder)
        ? rawOrder
        : rules.length;

    const condition = valueCompiler.compileInputValue(node.id, "value");

    rules.push({
      order,
      condition: condition as CompiledValue<boolean>,
      dependencies: condition.dependencies,
    });
  }

  return rules.sort((left, right) => left.order - right.order);
}

/**
 * Compiles `setSort` actions into list comparator rules.
 */
export function compileListSortRules(
  indexed: IndexedGraph,
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledListSortRule[] {
  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  const rules: CompiledListSortRule[] = [];

  for (const node of indexed.nodesById.values()) {
    if (!isSetSortNode(node)) {
      continue;
    }

    if (!hasConnectedValuePort(indexed, node.id)) {
      continue;
    }

    const rawOrder = readPortDefaultValue(node, ["order"]);
    const order =
      typeof rawOrder === "number" && Number.isFinite(rawOrder)
        ? rawOrder
        : rules.length;

    const comparator = valueCompiler.compileInputValue(node.id, "value");

    rules.push({
      order,
      comparator,
      dependencies: comparator.dependencies,
    });
  }

  return rules.sort((left, right) => left.order - right.order);
}
